/**
 * useInboxLiveQuery — TanStack Query hook for the LIVE inbox feed
 * (``GET /api/v1/inbox/live``).
 *
 * Sibling to ``useInboxQuery``. Where the latter pages over the DB-indexed
 * cross-channel feed (email + chat + PEC), this one talks to a BE that
 * forwards each request to the user's connected providers (Gmail /
 * Outlook / Aruba PEC) in real time. Provider is the source of truth for
 * read state — Ando just enriches each row with an ``is_ingested`` flag.
 *
 * Pagination — keyset cursor (``next_cursor``) wrapped in TanStack Query
 * v5 ``useInfiniteQuery``. Page size 100 (BE default; hard-cap 200).
 *
 * Cadence — ``staleTime: 30s`` + ``refetchOnWindowFocus: true``: the
 * user's mental model is "live = always fresh", so re-focusing the tab
 * (or remounting the route after navigating away for >30s) refetches
 * the first page. We do NOT poll on an interval — the BE call is
 * provider-bound and would burn quota.
 *
 * Defaults: when the query is loading or errored, ``items`` /
 * ``perAccount`` are empty arrays so consumers can render the chrome
 * without null-guards.
 */

import { infiniteQueryOptions, useInfiniteQuery, type InfiniteData } from "@tanstack/react-query";
import {
  listInboxLive,
  type InboxLiveItem,
  type InboxLivePerAccount,
  type InboxLiveResponse,
} from "@ando/core";

/** Page size — BE default; hard-cap is 200. */
const PAGE_SIZE = 100;

const EMPTY_ITEMS: readonly InboxLiveItem[] = [];
const EMPTY_PER_ACCOUNT: readonly InboxLivePerAccount[] = [];

export interface UseInboxLiveQueryResult {
  /** Flat list — every fetched page concatenated, BE order preserved. */
  readonly items: ReadonlyArray<InboxLiveItem>;
  /** Per-account diagnostics from the LATEST page (the BE rebuilds the
   *  full per-account summary on every page so this is always current). */
  readonly perAccount: ReadonlyArray<InboxLivePerAccount>;
  /** Initial fetch in flight (no pages yet). */
  readonly isLoading: boolean;
  readonly isError: boolean;
  /** Next-page fetch in flight. */
  readonly isFetchingNextPage: boolean;
  /** ``true`` while the BE keeps returning a non-null ``next_cursor``. */
  readonly hasNextPage: boolean;
  /** Idempotent — no-ops when already fetching or no more pages. */
  readonly fetchNextPage: () => void;
  readonly refetch: () => void;
}

export interface UseInboxLiveQueryOptions {
  /**
   * Smart-view filter pushed down to the provider's native query
   * (Gmail ``q=is:unread``, Graph ``$filter=isRead eq false``).
   * Supported: ``unread`` | ``starred`` | ``attachments``. ``tutti``
   * / ``today`` / ``needs_reply`` stay post-filters on the FE.
   */
  readonly view?: string;
  /**
   * Email direction (Jul 2026). ``"sent"`` fetches the provider's SENT folder
   * ("Inviate" view); omit / ``"received"`` for the INBOX.
   */
  readonly direction?: "sent" | "received";
  /**
   * Free-text search pushed to the provider (Gmail ``q=`` / Outlook
   * ``$search``) so search covers the whole mailbox and the infinite scroll
   * doesn't hunt the history over a client-side substring filter.
   */
  readonly q?: string;
}

/**
 * Shared infinite-query definition for the live inbox. Consumed by BOTH the
 * ``useInboxLiveQuery`` hook and the ``/workspace/conversations`` route loader
 * (``ensureInfiniteQueryData``) so hover/focus intent-preload warms the SAME
 * cache slice (keyed on view/direction/q) the component reads.
 */
export const inboxLiveInfiniteQueryOptions = (options: UseInboxLiveQueryOptions = {}) => {
  const view = options.view;
  const direction = options.direction;
  const q = options.q?.trim() || undefined;
  return infiniteQueryOptions<
    InboxLiveResponse,
    Error,
    InfiniteData<InboxLiveResponse, string | undefined>,
    readonly unknown[],
    string | undefined
  >({
    // Include ``view`` in the query key so switching smart-view triggers
    // a fresh fetch (the cache slice per view is isolated).
    queryKey: ["inbox", "live", view ?? null, direction ?? null, q ?? null] as const,
    queryFn: ({ pageParam }) =>
      listInboxLive({ limit: PAGE_SIZE, cursor: pageParam, view, direction, q }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

export function useInboxLiveQuery(
  options: UseInboxLiveQueryOptions = {},
): UseInboxLiveQueryResult {
  const query = useInfiniteQuery(inboxLiveInfiniteQueryOptions(options));

  // Flatten — pages preserve BE order so a single ``flatMap`` is enough.
  // Empty short-circuit avoids a new reference on every render when the
  // query is still loading (memo-friendly downstream).
  const items: ReadonlyArray<InboxLiveItem> = query.data
    ? query.data.pages.flatMap((p) => p.items)
    : EMPTY_ITEMS;

  // ``per_account`` is rebuilt on every page; reading from the LAST page
  // gives the freshest view of provider-level health.
  const lastPage = query.data?.pages[query.data.pages.length - 1];
  const perAccount = lastPage?.per_account ?? EMPTY_PER_ACCOUNT;

  return {
    items,
    perAccount,
    isLoading: query.isPending,
    isError: query.isError,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => {
      // Guard against re-entry — TanStack already debounces, but the
      // IntersectionObserver can fire multiple times before the request
      // resolves; this keeps the call site simple.
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
    refetch: () => {
      void query.refetch();
    },
  };
}
